// Příjem poptávek v1 (docs/PLAN.md 2.6, docs/LEADY-REKLAMY.md): zdroje s tokenem, webhook,
// hostovaný formulář, první odpověď. Bez session jsou jen /v1/hooks/leads/:token a jeho formulář.
import { z } from 'zod'
import { phoneInputSchema } from './org'

export const LEAD_SOURCE_TYPE_VALUES = [
  'web_form',
  'email',
  'make',
  'meta',
  'google',
  'telegram',
  'manual',
] as const

export const createLeadSourceSchema = z.object({
  type: z.enum(LEAD_SOURCE_TYPE_VALUES).default('web_form'),
  name: z.string().trim().min(2, 'Zadej název zdroje').max(120),
  config: z
    .object({
      /** Kam poslat návštěvníka po odeslání hostovaného formuláře. */
      redirectUrl: z
        .string()
        .url()
        .refine((u) => /^https?:\/\//i.test(u), 'Jen adresa http nebo https')
        .optional()
        .or(z.literal('')),
      /** Volitelné pole formuláře navíc k jménu, telefonu a zprávě. */
      fields: z.array(z.enum(['email', 'address'])).optional(),
    })
    .optional(),
})
export type CreateLeadSourceInput = z.infer<typeof createLeadSourceSchema>

export const updateLeadSourceSchema = createLeadSourceSchema.partial().extend({
  isActive: z.boolean().optional(),
})
export type UpdateLeadSourceInput = z.infer<typeof updateLeadSourceSchema>

export interface LeadSourceItem {
  id: string
  type: (typeof LEAD_SOURCE_TYPE_VALUES)[number]
  name: string
  token: string
  isActive: boolean
  config: { redirectUrl?: string; fields?: Array<'email' | 'address'> }
  /** Hotové adresy pro web: hostovaný formulář a webhook. */
  formUrl: string
  webhookUrl: string
  /** Adresa `leads+<token>@in.wacbuddy.cz` pro přeposílání z tržišť. */
  emailAddress: string
  /** Počet poptávek za posledních 30 dní. */
  leads30d: number
  createdAt: string
}

/** Tělo webhooku i hostovaného formuláře (POST /v1/hooks/leads/:token). Honeypot `website` musí být prázdný. */
export const leadSubmissionSchema = z.object({
  name: z.string().trim().min(2, 'Zadej jméno').max(160),
  phone: phoneInputSchema.optional().or(z.literal('')),
  email: z.string().trim().email('Zadej platný email').optional().or(z.literal('')),
  message: z.string().trim().min(3, 'Napiš, co potřebuješ').max(4000),
  address: z.string().trim().max(240).optional().or(z.literal('')),
  /** Honeypot: roboti ho vyplní, lidé ho nevidí. */
  website: z.string().max(0).optional(),
  /** Volné údaje z Make, Meta apod. (uloží se k interakci). */
  meta: z.record(z.string(), z.unknown()).optional(),
})
export type LeadSubmission = z.infer<typeof leadSubmissionSchema>

/** Odpověď webhooku: id zakázky a klienta, bez dalších dat (volá ji cizí strana). */
export interface LeadAccepted {
  ok: true
  jobId: string
  clientId: string
  /** Kam přesměrovat návštěvníka hostovaného formuláře (z konfigurace zdroje). */
  redirectUrl: string | null
}

/** GET /v1/hooks/leads/:token/form: co má hostovaný formulář zobrazit (bez session). */
export interface LeadFormInfo {
  orgName: string
  trade: string
  sourceName: string
  fields: Array<'email' | 'address'>
  /** Značka firmy z brand kitu (úkol 5.2); logo je podepsaná adresa s omezenou platností. */
  brand: LeadFormBrand
}

export interface LeadFormBrand {
  logoUrl: string | null
  primaryColor: string | null
  slogan: string | null
}

/** POST /v1/jobs/:id/first-response: první reakce na poptávku (SPEC 2.6, metrika first_response_at). */
export const FIRST_RESPONSE_KINDS = ['call', 'propose', 'reject'] as const
export const firstResponseSchema = z.object({
  kind: z.enum(FIRST_RESPONSE_KINDS),
})
export type FirstResponseInput = z.infer<typeof firstResponseSchema>
